import React from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Download } from 'lucide-react';

const ImportResultsModal = ({ results, onClose }) => {
    if (!results) return null;

    const { totalRows, successfulRows, failedRows, skippedRows, errors } = results;
    const hasErrors = errors && errors.length > 0;

    const downloadErrorReport = () => {
        if (!hasErrors) return;

        const csvContent = [
            ['Row', 'Item Code', 'Error'],
            ...errors.map(err => [err.rowNumber, err.itemCode || 'N/A', err.errorMessage])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'import_errors.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col animate-slideUp">
                {/* Header */}
                <div className={`px-6 py-4 ${failedRows > 0 ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gradient-to-r from-green-500 to-emerald-600'} text-white`}>
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-1.5 hover:bg-white/20 rounded-full transition-all"
                    >
                        <X size={20} />
                    </button>
                    <h2 className="text-xl font-bold pr-8">Import Results</h2>
                    <p className="text-sm opacity-90 mt-1">
                        {failedRows > 0 ? 'Import completed with errors' : 'Import completed successfully'}
                    </p>
                </div>

                {/* Summary Stats */}
                <div className="p-6 border-b border-gray-200">
                    <div className="grid grid-cols-4 gap-4">
                        <div className="text-center">
                            <div className="text-3xl font-bold text-gray-900">{totalRows}</div>
                            <div className="text-sm text-gray-600 mt-1">Total Rows</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl font-bold text-green-600">{successfulRows}</div>
                            <div className="text-sm text-gray-600 mt-1">Successful</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl font-bold text-red-600">{failedRows}</div>
                            <div className="text-sm text-gray-600 mt-1">Failed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl font-bold text-gray-400">{skippedRows}</div>
                            <div className="text-sm text-gray-600 mt-1">Skipped</div>
                        </div>
                    </div>
                </div>

                {/* Error Details */}
                {hasErrors && (
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <AlertTriangle size={20} className="text-orange-600" />
                                Error Details ({errors.length})
                            </h3>
                            <button
                                onClick={downloadErrorReport}
                                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-semibold"
                            >
                                <Download size={16} />
                                Download Report
                            </button>
                        </div>

                        <div className="space-y-2">
                            {errors.map((error, index) => (
                                <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <div className="flex items-start gap-3">
                                        <XCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-sm font-semibold text-red-900">Row {error.rowNumber}</span>
                                                {error.itemCode && (
                                                    <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded">
                                                        {error.itemCode}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-red-700">{error.errorMessage}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Success Message */}
                {!hasErrors && (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <div className="text-center">
                            <CheckCircle size={64} className="text-green-600 mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">All Products Imported!</h3>
                            <p className="text-gray-600">
                                {successfulRows} product{successfulRows !== 1 ? 's' : ''} successfully imported to your catalog.
                            </p>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-md hover:shadow-lg"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImportResultsModal;
